import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import MUITableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import qs from 'qs'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import TableLayout from '@/app/component/layout/table'
import TableCell from '@/app/component/layout/table/cell'
import TableRow from '@/app/component/layout/table/row'
import { usePaginated } from '@/app/hooks/use-paginated'
import { CAMPAIGN_FILTERABLE_ATTRIBUTES } from '@/app/module/campaigns/definitions'
import { CampaignQuery, TCampaignListItem } from '@/app/module/campaigns/types'
import { SelectOption, TableSpecs } from '@/app/types'

import CampaignTableRowActions from './actions'
import CampaignsTableRow from './row'
import CampaignsTableToolbar from './toolbar'

type Props = {
  data: Record<number, TCampaignListItem>
  headers: any
  loading: boolean
  loadingItems: number[]
  ordered: number[]
  page: number
  pageSizeValues?: SelectOption<number>[]
  query: CampaignQuery
  root: string
  specs: TableSpecs
  total: number

  createNotification: (data: Record<string, { type: string; message: string }>) => void
  onArchive: (itemId: number) => void
  onClose: (itemId: number) => void
  onCopy: (itemId: number) => void
  onOpen: (itemId: number) => void
  onPublish: (itemId: number) => void
  onRefresh: () => void
  onStopAllClick: (itemId: number) => void
  onUnpublish: (itemId: number) => void
  onUnpublishThenEdit: (itemId: number) => void
  onUnarchive: (itemId: number) => void
  toggleAbortConfirm: (data: any) => void
  toggleDownloadCampaignReport: (itemId: number) => void
}

const CampaignsTable: React.FC<Props> = (props) => {
  const navigate = useNavigate()

  const {
    data,
    headers,
    loading,
    loadingItems = [],
    ordered,
    page,
    pageSizeValues,
    query,
    root,
    specs,
    total,
    createNotification,
    onArchive,
    onClose,
    onCopy,
    onOpen,
    onPublish,
    onRefresh,
    onStopAllClick,
    onUnpublish,
    onUnpublishThenEdit,
    onUnarchive,
    toggleAbortConfirm,
    toggleDownloadCampaignReport,
  } = props

  const [toCopy, setToCopy] = React.useState<number | null>(null)
  const copyText = React.useRef<HTMLTextAreaElement>(null)

  const setPageSize = React.useCallback(
    (size: number) => {
      navigate(`/campaigns?${generateQuery({ ...query, size, page: 1 })}`)
    },
    [query, navigate],
  )

  const handleNavigate = React.useCallback(
    (nextQuery: CampaignQuery) => {
      navigate(`/campaigns?${generateQuery(nextQuery)}`)
    },
    [navigate],
  )

  const pagination = usePaginated({
    query,
    root,
    total,
    pageSizeValues,
    navigate: handleNavigate,
    setPageSize,
  })

  React.useEffect(() => {
    if (toCopy === null) {
      return
    }

    copyText.current?.select()
    navigator.clipboard.writeText(toCopy.toString()).then(() => {
      createNotification({
        'copy-id': {
          type: 'success',
          message: 'Campaign ID copied!',
        },
      })
      setToCopy(null)
    })
  }, [toCopy, createNotification])

  const onCreateCampaignsClick = React.useCallback(() => {
    navigate('/campaigns/create')
  }, [navigate])

  const onSetFilters = React.useCallback(
    ({ filters }: { filters: CampaignQuery }) => {
      const q = Object.keys(filters).reduce(
        (obj: CampaignQuery, key: string) => {
          if (!filters[key as keyof CampaignQuery] || key === 'page') {
            return obj
          }
          return {
            ...obj,
            [key]: filters[key as keyof CampaignQuery],
          }
        },
        {
          page: 1, // reset page every time a new filter is set
          size: filters.size,
        },
      )

      navigate(`/campaigns?${qs.stringify(q)}`)
    },
    [navigate],
  )

  const copyId = React.useCallback((id: number) => {
    setToCopy(id)
  }, [])

  return (
    <React.Fragment>
      <CampaignsTableToolbar
        filters={query}
        loading={loading}
        page={page}
        onCreateCampaignsClick={onCreateCampaignsClick}
        onRefresh={onRefresh}
        onSetFilters={onSetFilters}
      />
      <TableLayout
        loading={loading}
        pageSize={ordered.length}
        pagination={pagination}
        emptyMessage="No campaigns were found."
        style={{
          paddingRight: '128px',
        }}
      >
        <Table className="campaigns-table">
          <TableHead>
            <TableRow>
              {headers.map(({ title }: any, i: number) => (
                <TableCell key={i} index={i}>
                  {title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ordered.map((itemId: number) => (
              <CampaignsTableRow
                key={itemId}
                data={data[itemId]}
                headers={headers}
                loading={loadingItems.indexOf(itemId) > -1}
                specs={specs}
                onClick={() => navigate(`/campaigns/${itemId}`)}
              />
            ))}
          </TableBody>
        </Table>
        <Table
          className="campaign-actions-table"
          style={{
            position: 'absolute',
            background: 'white',
            top: '0px',
            right: '0px',
            width: '50px',
          }}
        >
          <TableHead>
            <TableRow>
              <MUITableCell
                style={{
                  height: '69px',
                }}
              >
                Actions
              </MUITableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordered.map((itemId) => (
              <TableRow isLoading={loading} key={itemId}>
                <MUITableCell
                  style={{
                    height: '69px',
                    boxShadow: '-1px 0px 1px 0px rgba(224,224,224,1)',
                  }}
                >
                  <div style={{ display: 'flex', maxWidth: '96px' }}>
                    <CampaignTableRowActions
                      data={data[itemId]}
                      actions={{
                        publish: () => onPublish(itemId),
                        unpublish: () => onUnpublish(itemId),
                        unpublishThenEdit: () => onUnpublishThenEdit(itemId),
                        archive: () => onArchive(itemId),
                        unarchive: () => onUnarchive(itemId),
                        abort: () => toggleAbortConfirm(data[itemId]),
                        open: () => onOpen(itemId),
                        close: () => onClose(itemId),
                        downloadCampaignReport: () => toggleDownloadCampaignReport(itemId),
                        copy: () => onCopy(itemId),
                        copyId: () => copyId(itemId),
                        manage: () => navigate(`/campaigns/manage/${itemId}`),
                        edit: () => navigate(`/campaigns/edit/${itemId}/content`),
                        stopAll: () => onStopAllClick(itemId),
                      }}
                    />
                  </div>
                </MUITableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableLayout>
      {toCopy && <textarea value={toCopy} ref={copyText} readOnly />}
    </React.Fragment>
  )
}

const generateQuery = (query: CampaignQuery) => {
  const q = CAMPAIGN_FILTERABLE_ATTRIBUTES.reduce(
    (obj, k) => {
      const key = k as keyof CampaignQuery
      if (!query[key]) {
        return obj
      }
      return {
        ...obj,
        [key]: query[key],
      }
    },
    {
      page: query.page,
      size: query.size,
    },
  )

  return qs.stringify(q)
}

export default CampaignsTable
